import {
  type FormEvent,
  type ReactNode,
  useMemo,
  useState,
} from "react";
import {
  ChatIcon,
  ChevronIcon,
  CloseIcon,
  DragIcon,
  FileIcon,
  TableIcon,
  TrashIcon,
} from "./Icons";
import type {
  ReviewCell,
  ReviewColumn,
  ReviewDocument,
  ReviewFileSource,
  ReviewRow,
  ReviewRunState,
  ReviewTableSettings,
  ReviewWrapMode,
} from "./types";
import "./review.css";

const COLUMN_TYPES: ReviewColumn["type"][] = [
  "Verbatim",
  "Summary",
  "Date",
  "Currency",
  "Yes / No",
];

const DEFAULT_COLUMN_PROMPT = `Add the following columns:
• Company name
• Counterparty name
• Agreement name
• Effective date
• Limitation of liability
• Indemnification`;

export interface ReviewTableWorkspaceProps {
  name: string;
  documents: ReviewDocument[];
  columns: ReviewColumn[];
  rows: ReviewRow[];
  settings: ReviewTableSettings;
  runState?: ReviewRunState;
  totalRowCount?: number;
  shownRowCount?: number;
  busy?: boolean;
  onRequestAddFile?: (source: ReviewFileSource) => void;
  onSuggestColumns?: (prompt: string) => Promise<ReviewColumn[]>;
  onAddColumns?: (columns: ReviewColumn[]) => void | Promise<void>;
  onUpdateSettings?: (settings: ReviewTableSettings) => void | Promise<void>;
  onUpdateColumns?: (columns: ReviewColumn[]) => void | Promise<void>;
  onOpenFilter?: () => void;
  onRun?: () => void | Promise<void>;
  onStop?: () => void | Promise<void>;
  onExport?: () => void | Promise<void>;
  onAskDI?: (prompt: string) => void | Promise<void>;
  onLoadMore?: () => void | Promise<void>;
  onOpenClause?: (
    cell: ReviewCell,
    row: ReviewRow,
    column: ReviewColumn
  ) => void;
}

export function ReviewTableWorkspace({
  name,
  documents,
  columns,
  rows,
  settings,
  runState = { status: "idle" },
  totalRowCount = rows.length,
  shownRowCount = rows.length,
  busy = false,
  onRequestAddFile,
  onSuggestColumns,
  onAddColumns,
  onUpdateSettings,
  onUpdateColumns,
  onOpenFilter,
  onRun,
  onStop,
  onExport,
  onAskDI,
  onLoadMore,
  onOpenClause,
}: ReviewTableWorkspaceProps) {
  const [wrapMode, setWrapMode] = useState<ReviewWrapMode>("compact");
  const [chatOpen, setChatOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [columnBuilderOpen, setColumnBuilderOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manageColumnsOpen, setManageColumnsOpen] = useState(false);

  const visibleColumns = useMemo(
    () => columns.filter((column) => column.visible !== false),
    [columns]
  );
  const running = runState.status === "running" || runState.status === "stopping";
  const runCallback = running ? onStop : onRun;
  const progress = Math.min(Math.max(runState.progress ?? 0, 0), 100);

  return (
    <section className="di-review di-review-table" aria-label={name}>
      <div className="di-review-table__workspace">
        <div className="di-table-toolbar" role="toolbar" aria-label="Review table tools">
          <span className="di-tag">{documents.length} documents</span>
          <div className="di-menu-anchor">
            <button
              aria-expanded={addMenuOpen}
              className="di-btn di-btn--secondary"
              onClick={() => setAddMenuOpen((open) => !open)}
              type="button"
            >
              <FileIcon />
              Add file
              <ChevronIcon direction="down" size={13} />
            </button>
            {addMenuOpen && (
              <div className="di-dropdown" role="menu" aria-label="Add documents">
                <div className="di-dropdown__heading">Documents to analyse</div>
                <div className="di-dropdown__drop">
                  <FileIcon size={20} />
                  <strong>Add another document</strong>
                  <span>Every document becomes a row in this table.</span>
                </div>
                {([
                  ["computer", "Browse this computer"],
                  ["folder", "Browse a folder"],
                  ["project", "Add from Project"],
                  ["repository", "Add from Contract Repository"],
                ] as Array<[ReviewFileSource, string]>).map(([source, label]) => (
                  <button
                    className="di-dropdown__item"
                    disabled={!onRequestAddFile}
                    key={source}
                    onClick={() => {
                      setAddMenuOpen(false);
                      onRequestAddFile?.(source);
                    }}
                    role="menuitem"
                    title={!onRequestAddFile ? "Document selection is not connected." : undefined}
                    type="button"
                  >
                    <FileIcon />
                    <span>{label}</span>
                    <span className="di-spacer" />
                    <ChevronIcon size={13} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="di-tag">{visibleColumns.length} columns</span>
          <button
            className="di-btn di-btn--secondary"
            onClick={() => setColumnBuilderOpen(true)}
            type="button"
          >
            <TableIcon />
            Column builder
          </button>
          <button
            className="di-btn di-btn--secondary"
            disabled={!onOpenFilter}
            onClick={onOpenFilter}
            title={!onOpenFilter ? "Filtering is not connected." : undefined}
            type="button"
          >
            Filter
          </button>
          <button
            className="di-btn di-btn--secondary"
            onClick={() => setSettingsOpen(true)}
            type="button"
          >
            Table settings
          </button>
          <button
            className="di-btn di-btn--secondary"
            onClick={() => setManageColumnsOpen(true)}
            type="button"
          >
            <TableIcon />
            Manage columns ({columns.length})
          </button>
          <WrapControl mode={wrapMode} onChange={setWrapMode} />
          <span className="di-spacer" />
          <button
            aria-pressed={chatOpen}
            className={`di-btn di-btn--secondary${chatOpen ? " is-active" : ""}`}
            onClick={() => setChatOpen((open) => !open)}
            type="button"
          >
            <ChatIcon />
            Chat
          </button>
          <button
            className="di-btn di-btn--primary"
            disabled={!runCallback || busy || runState.status === "stopping"}
            onClick={() => void runCallback?.()}
            title={!runCallback
              ? `${running ? "Stopping" : "Running"} the table is not connected.`
              : undefined}
            type="button"
          >
            <span className="di-run-glyph" aria-hidden="true">
              {running ? "■" : "▶"}
            </span>
            {running ? (runState.status === "stopping" ? "Stopping…" : "Stop") : "Run"}
          </button>
          <button
            className="di-btn di-btn--secondary"
            disabled={!onExport || busy}
            onClick={() => void onExport?.()}
            title={!onExport ? "Excel export is not connected." : undefined}
            type="button"
          >
            Export to Excel
          </button>
        </div>

        {running && (
          <div className="di-run-strip" role="status" aria-live="polite">
            <span className="di-spinner" />
            <span>
              {runState.label ||
                `Extracting ${visibleColumns.length} columns across ${documents.length} documents`}
            </span>
            <span className="di-progress" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </span>
            <span className="di-visually-hidden">{progress}% complete</span>
          </div>
        )}

        <div className="di-grid-scroll">
          <table className={`di-data-table di-data-table--${wrapMode}`}>
            <caption className="di-visually-hidden">
              Extracted review results for {name}
            </caption>
            <thead>
              <tr>
                <th scope="col">Document</th>
                {visibleColumns.map((column) => (
                  <th key={column.id} scope="col">{column.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="di-document-cell">
                      <span className="di-accent-icon"><FileIcon /></span>
                      {row.document.name}
                    </span>
                  </td>
                  {visibleColumns.map((column) => {
                    const cell = row.cells[column.id] || {};
                    return (
                      <td key={column.id}>
                        <CellContent
                          cell={cell}
                          column={column}
                          onOpenClause={onOpenClause}
                          row={row}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + 1}>
                    <span className="di-empty-row">
                      Run the table to generate evidence-backed cells.
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="di-table-footer">
          <span>
            Showing {shownRowCount} of {totalRowCount} documents · every cell
            links to the clause it was extracted from
          </span>
          <span className="di-spacer" />
          <button
            className="di-btn di-btn--secondary"
            disabled={!onLoadMore || shownRowCount >= totalRowCount}
            onClick={() => void onLoadMore?.()}
            title={!onLoadMore ? "Pagination is not connected." : undefined}
            type="button"
          >
            Load more
          </button>
        </div>
      </div>

      {chatOpen && (
        <AskDiPanel
          onAskDI={onAskDI}
          onClose={() => setChatOpen(false)}
        />
      )}

      {columnBuilderOpen && (
        <ColumnBuilderDialog
          onAddColumns={onAddColumns}
          onClose={() => setColumnBuilderOpen(false)}
          onSuggestColumns={onSuggestColumns}
        />
      )}
      {settingsOpen && (
        <TableSettingsDialog
          initialSettings={settings}
          onClose={() => setSettingsOpen(false)}
          onSave={onUpdateSettings}
        />
      )}
      {manageColumnsOpen && (
        <ManageColumnsDialog
          columns={columns}
          onClose={() => setManageColumnsOpen(false)}
          onOpenColumnBuilder={() => {
            setManageColumnsOpen(false);
            setColumnBuilderOpen(true);
          }}
          onSave={onUpdateColumns}
        />
      )}
    </section>
  );
}

function CellContent({
  cell,
  row,
  column,
  onOpenClause,
}: {
  cell: ReviewCell;
  row: ReviewRow;
  column: ReviewColumn;
  onOpenClause?: ReviewTableWorkspaceProps["onOpenClause"];
}) {
  if (cell.status === "generating") {
    return (
      <span className="di-cell di-cell--generating">
        <span className="di-spinner" />
        Generating output…
      </span>
    );
  }
  if (cell.status === "failed") {
    return <span className="di-cell di-cell--failed">Needs review</span>;
  }
  return (
    <span className="di-cell">
      <span>{cell.value || "—"}</span>
      {cell.clauseRef && (
        <button
          className="di-citation"
          disabled={!onOpenClause}
          onClick={() => onOpenClause?.(cell, row, column)}
          title={!onOpenClause ? "Clause navigation is not connected." : `Open ${cell.clauseRef}`}
          type="button"
        >
          {cell.clauseRef}
        </button>
      )}
    </span>
  );
}

function WrapControl({
  mode,
  onChange,
}: {
  mode: ReviewWrapMode;
  onChange: (mode: ReviewWrapMode) => void;
}) {
  const labels: Array<[ReviewWrapMode, string, string]> = [
    ["nowrap", "No wrap", "☰"],
    ["compact", "Compact wrap", "≡"],
    ["full", "Full wrap", "☷"],
  ];
  return (
    <div className="di-segmented" aria-label="Cell wrapping">
      {labels.map(([value, label, glyph]) => (
        <button
          aria-label={label}
          aria-pressed={mode === value}
          key={value}
          onClick={() => onChange(value)}
          title={label}
          type="button"
        >
          {glyph}
        </button>
      ))}
    </div>
  );
}

export interface ColumnBuilderDialogProps {
  onClose: () => void;
  onSuggestColumns?: (prompt: string) => Promise<ReviewColumn[]>;
  onAddColumns?: (columns: ReviewColumn[]) => void | Promise<void>;
}

export function ColumnBuilderDialog({
  onClose,
  onSuggestColumns,
  onAddColumns,
}: ColumnBuilderDialogProps) {
  const [prompt, setPrompt] = useState(DEFAULT_COLUMN_PROMPT);
  const [columns, setColumns] = useState<ReviewColumn[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const selectedColumns = columns.filter((column) => selected[column.id] !== false);

  async function generate() {
    if (!onSuggestColumns) return;
    setGenerating(true);
    setError("");
    try {
      const next = await onSuggestColumns(prompt);
      setColumns(next);
      setSelected(Object.fromEntries(next.map((column) => [column.id, true])));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Columns could not be generated.");
    } finally {
      setGenerating(false);
    }
  }

  function patchColumn(id: string, patch: Partial<ReviewColumn>) {
    setColumns((current) => current.map((column) =>
      column.id === id ? { ...column, ...patch } : column
    ));
  }

  return (
    <DialogShell
      footer={(
        <>
          <span className="di-muted">
            {columns.length === 0
              ? "Generate columns to review them before adding"
              : `${selectedColumns.length} of ${columns.length} columns selected`}
          </span>
          <span className="di-spacer" />
          <button className="di-btn di-btn--secondary" onClick={onClose} type="button">Cancel</button>
          <button
            className="di-btn di-btn--primary"
            disabled={!onAddColumns || selectedColumns.length === 0}
            onClick={() => {
              void Promise.resolve(onAddColumns?.(selectedColumns)).then(onClose);
            }}
            title={!onAddColumns ? "Adding columns is not connected." : undefined}
            type="button"
          >
            Add columns
          </button>
        </>
      )}
      onClose={onClose}
      size="wide"
      title="Column builder"
    >
      <p>Ask DI to generate several columns at once from a single prompt.</p>
      <textarea
        className="di-input di-textarea di-column-prompt"
        onChange={(event) => setPrompt(event.target.value)}
        value={prompt}
      />
      <div className="di-inline-help">
        <span>
          Name the document type you are reviewing and the information to
          extract. DI writes the query and picks the output type for each column.
        </span>
        <button
          className="di-btn di-btn--primary"
          disabled={!onSuggestColumns || generating || prompt.trim().length === 0}
          onClick={() => void generate()}
          title={!onSuggestColumns ? "Column suggestions are not connected." : undefined}
          type="button"
        >
          {generating ? "Generating…" : "Generate columns"}
        </button>
      </div>
      {error && <p className="di-error" role="alert">{error}</p>}
      <div className="di-column-builder-grid">
        <div className="di-column-builder-grid__head" aria-hidden="true">
          <span />
          <span />
          <span>Title</span>
          <span>Query</span>
          <span>Type</span>
          <span />
        </div>
        {columns.length === 0 ? (
          <div className="di-column-builder-grid__empty">
            <strong>No columns generated yet.</strong>
            <span>Generated columns appear here for review before they are added to the table.</span>
          </div>
        ) : columns.map((column) => (
          <div className="di-column-builder-grid__row" key={column.id}>
            <span className="di-drag-handle"><DragIcon /></span>
            <SquareCheck
              checked={selected[column.id] !== false}
              label={`Include ${column.title}`}
              onChange={(checked) => setSelected((current) => ({
                ...current,
                [column.id]: checked,
              }))}
            />
            <input
              aria-label={`${column.title} title`}
              className="di-input"
              onChange={(event) => patchColumn(column.id, { title: event.target.value })}
              value={column.title}
            />
            <textarea
              aria-label={`${column.title} query`}
              className="di-input"
              onChange={(event) => patchColumn(column.id, { query: event.target.value })}
              value={column.query}
            />
            <select
              aria-label={`${column.title} type`}
              className="di-input"
              onChange={(event) => patchColumn(column.id, {
                type: event.target.value as ReviewColumn["type"],
              })}
              value={column.type}
            >
              {COLUMN_TYPES.map((type) => <option key={type}>{type}</option>)}
            </select>
            <button
              aria-label={`Remove ${column.title}`}
              className="di-icon-button"
              onClick={() => setColumns((current) =>
                current.filter((item) => item.id !== column.id)
              )}
              type="button"
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
    </DialogShell>
  );
}

export interface TableSettingsDialogProps {
  initialSettings: ReviewTableSettings;
  onClose: () => void;
  onSave?: (settings: ReviewTableSettings) => void | Promise<void>;
}

export function TableSettingsDialog({
  initialSettings,
  onClose,
  onSave,
}: TableSettingsDialogProps) {
  const [settings, setSettings] = useState(initialSettings);
  const patch = <K extends keyof ReviewTableSettings>(
    key: K,
    value: ReviewTableSettings[K]
  ) => setSettings((current) => ({ ...current, [key]: value }));

  return (
    <DialogShell
      footer={(
        <>
          <span className="di-muted">Saved settings apply on the next run.</span>
          <span className="di-spacer" />
          <button className="di-btn di-btn--secondary" onClick={onClose} type="button">Cancel</button>
          <button
            className="di-btn di-btn--primary"
            disabled={!onSave}
            onClick={() => {
              void Promise.resolve(onSave?.(settings)).then(onClose);
            }}
            title={!onSave ? "Saving table settings is not connected." : undefined}
            type="button"
          >
            Save
          </button>
        </>
      )}
      onClose={onClose}
      size="medium"
      title="Table settings"
    >
      <label className="di-field">
        <span>Instructions</span>
        <textarea
          className="di-input di-textarea"
          maxLength={10000}
          onChange={(event) => patch("instructions", event.target.value)}
          value={settings.instructions}
        />
        <small className="di-field__split">
          <span>Matter background, document conventions and output preferences.</span>
          <span>{settings.instructions.length.toLocaleString()} / 10,000</span>
        </small>
      </label>
      <div className="di-dialog-rule" />
      <DialogSettingSelect
        label="Language"
        onChange={(value) => patch("language", value)}
        options={["English (United Kingdom)", "English (United States)", "German", "French"]}
        value={settings.language}
      />
      <h3 className="di-kicker di-kicker--ink">Global formatting</h3>
      <DialogSettingSelect
        label="Date"
        onChange={(value) => patch("dateFormat", value)}
        options={["Default — as written", "31 December 2026", "2026-12-31"]}
        value={settings.dateFormat}
      />
      <DialogSettingSelect
        label="Currency"
        onChange={(value) => patch("currencyFormat", value)}
        options={["Default — as written", "EUR · €1,250,000", "GBP · £1,250,000"]}
        value={settings.currencyFormat}
      />
      <DialogSettingSelect
        label="Numbers"
        onChange={(value) => patch("numberFormat", value)}
        options={["Default — as written", "1,250.00", "1.250,00"]}
        value={settings.numberFormat}
      />
    </DialogShell>
  );
}

function DialogSettingSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="di-settings-row">
      <span>{label}</span>
      <select
        className="di-input"
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}

export interface ManageColumnsDialogProps {
  columns: ReviewColumn[];
  onClose: () => void;
  onOpenColumnBuilder: () => void;
  onSave?: (columns: ReviewColumn[]) => void | Promise<void>;
}

export function ManageColumnsDialog({
  columns,
  onClose,
  onOpenColumnBuilder,
  onSave,
}: ManageColumnsDialogProps) {
  const [working, setWorking] = useState(columns);
  const shown = working.filter((column) => column.visible !== false).length;
  function patch(id: string, update: Partial<ReviewColumn>) {
    setWorking((current) => current.map((column) =>
      column.id === id ? { ...column, ...update } : column
    ));
  }

  return (
    <DialogShell
      footer={(
        <>
          <span className="di-muted">{shown} of {working.length} columns shown</span>
          <span className="di-spacer" />
          <button className="di-btn di-btn--secondary" onClick={onOpenColumnBuilder} type="button">
            Column builder
          </button>
          <button
            className="di-btn di-btn--primary"
            disabled={!onSave}
            onClick={() => {
              void Promise.resolve(onSave?.(working)).then(onClose);
            }}
            title={!onSave ? "Column management is not connected." : undefined}
            type="button"
          >
            Done
          </button>
        </>
      )}
      onClose={onClose}
      size="small"
      title="Manage columns"
    >
      <div className="di-manage-columns">
        {working.map((column) => (
          <div className="di-manage-columns__row" key={column.id}>
            <span className="di-drag-handle"><DragIcon /></span>
            <SquareCheck
              checked={column.visible !== false}
              disabled={!onSave}
              label={`Show ${column.title}`}
              onChange={(visible) => patch(column.id, { visible })}
            />
            <span>{column.title}</span>
            <span className="di-tag">{column.type}</span>
            <button
              aria-label={`Remove ${column.title}`}
              className="di-icon-button"
              disabled={!onSave}
              onClick={() => setWorking((current) =>
                current.filter((item) => item.id !== column.id)
              )}
              title={!onSave ? "Column removal is not connected." : undefined}
              type="button"
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
    </DialogShell>
  );
}

export interface AskDiPanelProps {
  onClose: () => void;
  onAskDI?: (prompt: string) => void | Promise<void>;
}

export function AskDiPanel({ onClose, onAskDI }: AskDiPanelProps) {
  const [prompt, setPrompt] = useState("");
  const [sending, setSending] = useState(false);
  const suggestions = [
    "Which agreements cap liability below 12 months' fees?",
    "Add a column for renewal notice",
    "Summarise the outliers",
  ];

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!onAskDI || !prompt.trim()) return;
    setSending(true);
    try {
      await onAskDI(prompt.trim());
      setPrompt("");
    } finally {
      setSending(false);
    }
  }

  return (
    <aside className="di-ask-panel" aria-label="Ask Document Intelligence">
      <div className="di-ask-panel__header">
        <h2>Ask DI</h2>
        <button aria-label="Close Ask DI" className="di-icon-button" onClick={onClose} type="button">
          <CloseIcon />
        </button>
      </div>
      <div className="di-ask-panel__body">
        <p className="di-muted">
          Ask anything about the documents in this table, or tell DI to add,
          rewrite or remove columns. Answers cite the clause they came from.
        </p>
        <div className="di-suggestion-row">
          {suggestions.map((suggestion) => (
            <button
              className="di-tag di-tag--outline"
              key={suggestion}
              onClick={() => setPrompt(suggestion)}
              type="button"
            >
              {suggestion}
            </button>
          ))}
        </div>
        <div className="di-chat-turn">
          <h3>You · 14:38</h3>
          <p>
            Which of these agreements would let the counterparty walk away on a
            change of control, and what notice do they get?
          </p>
        </div>
        <div className="di-chat-turn">
          <h3 className="di-kicker">
            Document Intelligence · 14:38
          </h3>
          <p>
            Three: Halden GmbH (30 days from notice, §9.3), Meridian Bank
            (consent plus re-approval, §22.3) and Sable Logistics (§8.4).
            Halden is the only one where the right survives a consent request.
          </p>
          <div className="di-citation-row">
            {["§9.3", "§22.3", "§8.4"].map((clause) => (
              <span className="di-citation" key={clause}>{clause}</span>
            ))}
          </div>
        </div>
      </div>
      <form
        className="di-ask-panel__composer"
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <textarea
          aria-label="Ask DI about this table"
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="Ask about the documents or edit the table…"
          value={prompt}
        />
        <div>
          <span className="di-muted">Reads this review table</span>
          <button
            className="di-btn di-btn--primary"
            disabled={!onAskDI || !prompt.trim() || sending}
            title={!onAskDI ? "Table chat is not connected." : undefined}
            type="submit"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </aside>
  );
}

function SquareCheck({
  checked,
  disabled = false,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      aria-label={label}
      aria-pressed={checked}
      className="di-square-check"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      type="button"
    >
      <span>{checked && <span />}</span>
    </button>
  );
}

function DialogShell({
  title,
  size,
  onClose,
  children,
  footer,
}: {
  title: string;
  size: "small" | "medium" | "wide";
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  const headingId = `di-dialog-${title.toLowerCase().replace(/\W+/g, "-")}`;
  return (
    <div className="di-dialog-backdrop">
      <section
        aria-labelledby={headingId}
        aria-modal="true"
        className={`di-dialog di-dialog--${size}`}
        role="dialog"
      >
        <header className="di-dialog__header">
          <h2 id={headingId}>{title}</h2>
          <button aria-label={`Close ${title}`} className="di-icon-button" onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </header>
        <div className="di-dialog__body">{children}</div>
        <footer className="di-dialog__footer">{footer}</footer>
      </section>
    </div>
  );
}
